import { useEffect, useLayoutEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { Sun, Moon } from "lucide-react";

const STORAGE_KEY = "blog-reading-mode";
const CLASS_NAME = "reading-light";

function readInitial(): "light" | "dark" {
  if (typeof window === "undefined") return "dark";
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

function applyMode(mode: "light" | "dark") {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle(CLASS_NAME, mode === "light");
}

// useLayoutEffect on the client, no-op on the server (avoids SSR warning).
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

function isBlogPostPath(pathname: string) {
  if (!pathname.startsWith("/blog/")) return false;
  if (pathname === "/blog/tags") return false;
  return true;
}

/**
 * Always-mounted controller. Synchronously syncs the `reading-light` class on
 * <html> with the current route + saved preference *before* paint, so there is
 * no flash of the wrong theme during navigation.
 */
export function ReadingModeController() {
  const routeState = useRouterState({
    select: (s) => ({
      pathname: s.location.pathname,
      isBlogPostRendered: s.matches.some((match) => match.routeId === "/blog/$slug"),
    }),
  });
  useIsoLayoutEffect(() => {
    const wantLight =
      isBlogPostPath(routeState.pathname) &&
      routeState.isBlogPostRendered &&
      readInitial() === "light";
    applyMode(wantLight ? "light" : "dark");
  }, [routeState]);
  return null;
}

function useReadingMode() {
  const [mode, setMode] = useState<"light" | "dark">("dark");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const initial = readInitial();
    setMode(initial);
    applyMode(initial);
    setMounted(true);
  }, []);

  const toggle = () => {
    const next = mode === "light" ? "dark" : "light";
    setMode(next);
    applyMode(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* noop */
    }
  };

  return { mode, toggle, mounted };
}

function useCanUseReadingModeRoute() {
  const routeState = useRouterState({
    select: (s) => ({
      pathname: s.location.pathname,
      isBlogPostRendered: s.matches.some((match) => match.routeId === "/blog/$slug"),
    }),
  });
  return isBlogPostPath(routeState.pathname) && routeState.isBlogPostRendered;
}

function useIsDesktop(minWidth = 900) {
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${minWidth}px)`);
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [minWidth]);
  return isDesktop;
}

type Variant = "floating" | "inline";

interface Props {
  variant: Variant;
}

export function ReadingModeToggle({ variant }: Props) {
  const canUseReadingMode = useCanUseReadingModeRoute();
  const isDesktop = useIsDesktop();

  if (!canUseReadingMode) return null;
  if (variant === "floating" && !isDesktop) return null;
  if (variant === "inline" && isDesktop) return null;

  return <ReadingModeButton variant={variant} />;
}

function ReadingModeButton({ variant }: Props) {
  const { mode, toggle, mounted } = useReadingMode();

  const label = mounted
    ? `Switch to ${mode === "light" ? "dark" : "light"} reading mode`
    : "Toggle reading mode";

  const Icon = mounted && mode === "light" ? Moon : Sun;

  if (variant === "floating") {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={label}
        title={label}
        className="reading-mode-fab no-print fixed right-4 top-1/2 z-40 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full backdrop-blur-md transition-all hover:scale-105"
      >
        <Icon className="h-4 w-4" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      title={label}
      className="no-print mr-1 inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-primary"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
